import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  /** Optional override fallback UI (defaults to GearVN-styled error card) */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * React error boundary — catches runtime errors in any descendant component tree
 * and renders a fallback UI instead of unmounting the whole app to a blank screen.
 *
 * Wrap top-level routes (or sections that may contain bug-prone third-party widgets
 * like charts) so a crash in one stays contained.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface to console for debugging — could pipe to Sentry/CF Workers logs later.
    console.error('[ErrorBoundary]', error, info);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback(error, this.reset);
    }

    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <div className="w-full max-w-md rounded-lg border bg-card p-6 text-center shadow-sm">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <h1 className="mt-4 text-lg font-semibold">Có lỗi xảy ra</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Một thành phần trên trang gặp lỗi không mong muốn. Cậu thử reload xem có hết không —
            nếu vẫn lỗi, báo team với thông tin bên dưới.
          </p>
          <pre className="mt-4 max-h-40 overflow-auto rounded-md bg-muted/50 p-2 text-left text-[11px] text-muted-foreground">
            {error.message}
          </pre>
          <div className="mt-4 flex justify-center gap-2">
            <Button size="sm" variant="outline" onClick={this.reset}>
              <RotateCcw className="h-4 w-4" />
              Thử lại
            </Button>
            <Button size="sm" onClick={() => window.location.reload()}>
              Reload trang
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
